import { Box, Button, Stack, Typography } from '@mui/material'
import { Header, ScopeRequirementAll, SCOPES } from 'components'

import { observeCustomers, observeRoles, observeUserRoles, useCurrentUser } from 'hooks'
import { useGetUsers } from 'pages/admin/common/adminDatastore'
import { defaultRoutes } from 'pages/AllRoutes'
import { useMemo, useState } from 'react'
import { UsersTable } from './components/UsersTable'
import { AddUser } from './components/AddUser'

export const UsersPage = () => {
  const { currentUser } = useCurrentUser()

  const routes = useMemo(() => {
    return defaultRoutes({
      isAdmin: currentUser.isAdmin,
      canAccessRoles: currentUser.canAccessRoles,
      canAccessUsers: currentUser.canAccessUsers
    })
  }, [currentUser.isAdmin, currentUser.canAccessRoles, currentUser.canAccessUsers])

  const [, roles] = observeRoles()
  const [, userRoles] = observeUserRoles()
  const customers = observeCustomers()
  const { usersQuery } = useGetUsers(currentUser.accessToken)

  const [user, setUser] = useState()
  const [openUserModal, setOpenUserModal] = useState(false)
  const hasCognitoAdmin = currentUser.isAdmin

  const userRows = useMemo(() => {
    const users_ = usersQuery.data?.map((item) => {
      const companyIds = item.Attributes?.find((x) => x.Name === 'customerName')?.Value
      const siteIds = item.Attributes?.find((x) => x.Name === 'sites')?.Value
      const sandvikId = item.Attributes?.find((x) => x.Name === 'sandvikUserId')?.Value
      const username = item.Username

      let parsedCompanyIds = []
      try {
        const parsed = JSON.parse(companyIds)
        parsedCompanyIds = parsed
      } catch {
        parsedCompanyIds = [companyIds]
      }
      const parsedSiteIds = siteIds?.length ? JSON.parse(siteIds) : []
      const companyNames = customers
        .filter((c) => parsedCompanyIds.includes(c.id))
        .map((c) => c.Name)
        .join(', ')

      const ownUserRoles = userRoles.filter((u) => [username, sandvikId].includes(u.CognitoId)).map((u) => u.RoleId)
      const ownRoles = roles.filter((r) => ownUserRoles.includes(r.id))

      return {
        Username: username,
        SandvikUserId: sandvikId,
        CompanyIds: parsedCompanyIds,
        SiteIds: parsedSiteIds,
        CompanyNames: companyNames,
        FirstName: item?.Attributes?.find((x) => x.Name === 'given_name')?.Value,
        LastName: item?.Attributes?.find((x) => x.Name === 'family_name')?.Value,
        Email: item?.Attributes?.find((x) => x.Name === 'email')?.Value,
        RoleIds: ownRoles.map((r) => r.id),
        Roles: ownRoles.map((r) => r.Name).join(', '),
        Enabled: item?.Enabled
      }
    })

    if (currentUser.isAdmin) {
      return users_
    }
    const siteIds = currentUser?.getSitesForScope(SCOPES.userManagement.Write)
    return users_.filter(
      (u) => u.CompanyIds.some((c) => currentUser.customerIds.includes(c)) && u.SiteIds.some((s) => siteIds.includes(s))
    )
  }, [usersQuery.data, currentUser, roles, userRoles.length])

  const filteredRoles = useMemo(() => {
    if (currentUser.isAdmin) {
      return roles
    }
    const siteIds = currentUser?.getSitesForScope(SCOPES.userManagement.Write)

    return roles.filter((r) => r.SiteIds?.some((s) => siteIds.includes(s)))
  }, [roles, currentUser])

  return (
    <Box sx={{ width: '100%', height: '100%', px: '15px' }}>
      <Header routes={routes} />

      <Stack spacing={2}>
        <Stack sx={{ pt: 2 }} direction="row" alignItems="center">
          <Typography variant="h1">Manage users</Typography>

          {hasCognitoAdmin && (
            <ScopeRequirementAll requirements={[SCOPES.userManagement.Write]}>
              <Button
                sx={{
                  ml: '1em'
                }}
                component="label"
                variant="contained"
                size="small"
                color="secondary"
                onClick={() => {
                  setUser(undefined)
                  setOpenUserModal(true)
                }}
                tabIndex={-1}>
                Add user
              </Button>
            </ScopeRequirementAll>
          )}
        </Stack>
      </Stack>

      {!hasCognitoAdmin && currentUser.hasAllScopes([SCOPES.userManagement.Write]) && (
        <Typography color="error">
          Your role has User write access, but does not have Admin Cognito permission. Adding/editing Users will be
          disabled.
        </Typography>
      )}

      <UsersTable
        setUser={(user) => {
          setUser(user)
          setOpenUserModal(true)
        }}
        isLoading={usersQuery.isFetching}
        data={userRows}
      />

      {openUserModal && (
        <AddUser
          closeModal={() => setOpenUserModal(false)}
          data={user}
          roles={filteredRoles}
          refetch={usersQuery.refetch}
          customers={customers}
        />
      )}
    </Box>
  )
}
